import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import { NotFound } from '@components/not-found';
import { ROUTES } from './constants';
import RequireAuth from './RequireAuth';
import MainPageRote from './MainPageRote';
import RedirectIFAuth from './RedirectIfAuth';
import ProfilePageRoute from './ProfilePageRoute';
import { useAppSelector } from '@hooks/redux/redux';
import { isSaaS } from '@/lib/utils/access';
import { isFeature } from '@utils/feature';

// — Named exports —
const Login = lazy(() => import('../pages/login').then((m) => ({ default: m.Login })));
const Register = lazy(() => import('../pages/register').then((m) => ({ default: m.Register })));
const Users = lazy(() => import('../pages/users').then((m) => ({ default: m.Users })));
const Filials = lazy(() => import('../pages/filials').then((m) => ({ default: m.Filials })));
const Roles = lazy(() => import('../pages/roles').then((m) => ({ default: m.Roles })));
const Profile = lazy(() =>
  import('@/containers/pages/user/feature/edit-user').then((m) => ({ default: m.Profile }))
);
const Dashboard = lazy(() => import('../pages/dashboard').then((m) => ({ default: m.Dashboard })));
const Statistics = lazy(() =>
  import('../pages/statistics').then((m) => ({ default: m.Statistics }))
);
const Home = lazy(() => import('../pages/home').then((m) => ({ default: m.Home })));
const Activations = lazy(() =>
  import('@/containers/pages/activations').then((m) => ({ default: m.Activations }))
);
const Subscription = lazy(() =>
  import('../pages/subscriptions').then((m) => ({ default: m.Subscription }))
);
const Licenses = lazy(() => import('@components/licenses').then((m) => ({ default: m.Licenses })));
const ResetPassword = lazy(() =>
  import('@/containers/pages/reser-Password').then((m) => ({ default: m.ResetPassword }))
);
const RequestResetPassword = lazy(() =>
  import('@/containers/pages/requestResetPassword').then((m) => ({
    default: m.RequestResetPassword
  }))
);
const ConfirmEmail = lazy(() =>
  import('../pages/confirmEmail').then((m) => ({ default: m.ConfirmEmail }))
);
const InviteRegister = lazy(() =>
  import('@/containers/pages/invite-register').then((m) => ({ default: m.InviteRegister }))
);
const ProfileLocale = lazy(() =>
  import('../pages/user/feature/edit-user/components/tabs/locale').then((m) => ({
    default: m.ProfileLocale
  }))
);
const EventsTrailPage = lazy(() =>
  import('@/containers/pages/commonSettings/tabs/EventsTrailPage').then((m) => ({
    default: m.EventsTrailPage
  }))
);

// — Default exports —
const FilialEdit = lazy(() => import('../pages/filials/edit'));
const FilialSettings = lazy(() => import('../pages/filials/edit/components/tabs/settings'));
const FilialSecurity = lazy(() => import('../pages/filials/edit/components/tabs/security'));
const FilialSMTP = lazy(() => import('../pages/filials/edit/components/tabs/smtp'));
const FilialLDAP = lazy(() => import('../pages/filials/edit/components/tabs/ldap'));
const EmailModule = lazy(() => import('../pages/filials/edit/components/tabs/email'));
const CalendarModule = lazy(() => import('../pages/filials/edit/components/tabs/calendar'));
const DrawModule = lazy(() => import('../pages/filials/edit/components/tabs/draw'));
const MailServersPage = lazy(() => import('../pages/filials/edit/components/tabs/mail-servers'));
const MailingSetupPage = lazy(
  () => import('@/containers/pages/filials/edit/components/tabs/mailing-setup')
);
const LocaleTimezonePage = lazy(
  () => import('@/containers/pages/filials/edit/components/tabs/locale-timezone')
);
const DiskSpasePage = lazy(
  () => import('@/containers/pages/filials/edit/components/tabs/disk-space')
);
const SetupQuota = lazy(
  () => import('../pages/filials/edit/components/tabs/disk-space/components/setup-quota')
);
const InviteLinksList = lazy(
  () => import('@/containers/pages/filials/edit/components/tabs/invite-links')
);
const ContactsModule = lazy(
  () => import('@/containers/pages/filials/edit/components/tabs/contacts')
);
const ProjectsModule = lazy(
  () => import('@/containers/pages/filials/edit/components/tabs/projects')
);
const PagesModule = lazy(() => import('../pages/filials/edit/components/tabs/pages'));
const FormsModule = lazy(() => import('../pages/filials/edit/components/tabs/forms'));
const CustomPage = lazy(
  () => import('@/containers/pages/filials/edit/components/tabs/custom-page')
);
const PersonalData = lazy(
  () => import('@/containers/pages/user/feature/edit-user/components/tabs/personalData')
);
const FilialData = lazy(
  () => import('@/containers/pages/user/feature/edit-user/components/tabs/filialData')
);
const Safety = lazy(
  () => import('@/containers/pages/user/feature/edit-user/components/tabs/safety')
);
const MailAccount = lazy(
  () => import('@/containers/pages/user/feature/edit-user/components/tabs/mailAccount')
);
const DiskData = lazy(
  () => import('@/containers/pages/user/feature/edit-user/components/tabs/diskData')
);
const CommonSettingsPage = lazy(() => import('../pages/commonSettings'));
const IntegrationsPage = lazy(() => import('@/containers/pages/commonSettings/tabs/integrations'));
const BrandingPage = lazy(() => import('@/containers/pages/commonSettings/tabs/branding'));
const SsoSettingsPage = lazy(() => import('@/containers/pages/commonSettings/tabs/ssoSettings'));
const ProductNotificationsPage = lazy(() => import('../pages/commonSettings/tabs/notifications'));
const StoragePage = lazy(() => import('../pages/commonSettings/tabs/StoragePage'));
const Activity = lazy(() => import('../pages/statistics/components/tabs/activity'));
const DiskSpace = lazy(() => import('../pages/statistics/components/tabs/disk-space'));
const Audit = lazy(() => import('../pages/statistics/components/tabs/audit'));
const NotificationsPage = lazy(() => import('#/pages/profile/notifications/notifications.page'));
const NotificationSettingsPage = lazy(
  () => import('#/pages/profile/notification-settings/notification-settings.page')
);

const isPreviewBuild = import.meta.env.VITE_PREVIEW_BUILD === 'true';
const previewUUID = import.meta.env.VITE_PREVIEW_UUID;
const basenameOption = isPreviewBuild ? `/${previewUUID}/` : '';

const PageLoader = () => (
  <Center h="100vh">
    <Loader size="md" />
  </Center>
);

export const MainRoutes = () => {
  const { Settings } = useAppSelector((state) => state.userInfo);
  return (
    <BrowserRouter basename={basenameOption}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route element={<RequireAuth />}>
            <Route path="/" element={<MainPageRote />}>
              <Route path={ROUTES.users.fullPath} element={<Users />} />
            </Route>
            <Route path="/profile" element={<ProfilePageRoute />} />
            <Route path={ROUTES.profile.fullPath} element={<Profile />}>
              <Route path={ROUTES.profile.pages.personalData} element={<PersonalData />} />
              <Route path={ROUTES.profile.pages.filials} element={<FilialData />} />
              <Route path={ROUTES.profile.pages.safety} element={<Safety />} />
              <Route path={ROUTES.profile.pages.mailAccount} element={<MailAccount />} />
              <Route path={ROUTES.profile.pages.disk} element={<DiskData />} />
              {isSaaS(Settings) && (
                <Route path={ROUTES.profile.pages.licenses} element={<Licenses />} />
              )}
              <Route path={ROUTES.profile.pages.locale} element={<ProfileLocale />} />
              <Route path={ROUTES.profile.pages.notifications} element={<NotificationsPage />} />
              <Route
                path={ROUTES.profile.pages.notificationSettings}
                element={<NotificationSettingsPage />}
              />
            </Route>
            <Route path={ROUTES.commonSettings.fullPath} element={<CommonSettingsPage />}>
              {isFeature() && (
                <Route path={ROUTES.commonSettings.pages.branding} element={<BrandingPage />} />
              )}
              {isSaaS(Settings) && (
                <Route
                  path={ROUTES.commonSettings.pages.notifications}
                  element={<ProductNotificationsPage />}
                />
              )}
              <Route
                path={ROUTES.commonSettings.pages.integrations}
                element={<IntegrationsPage />}
              />
              <Route path={ROUTES.commonSettings.pages.sso} element={<SsoSettingsPage />} />
              <Route path={ROUTES.commonSettings.pages.auditTrail} element={<EventsTrailPage />} />
              <Route path={ROUTES.commonSettings.pages.storage} element={<StoragePage />} />
            </Route>
            <Route path={ROUTES.dashboard.fullPath} element={<Dashboard />} />
            <Route path={ROUTES.home.fullPath} element={<Home />} />
            <Route path={ROUTES.roles.fullPath} element={<Roles />} />
            {isSaaS(Settings) && (
              <Route path={ROUTES.activations.fullPath} element={<Activations />} />
            )}
            {isSaaS(Settings) && (
              <Route path={ROUTES.subscriptions.fullPath} element={<Subscription />} />
            )}
            <Route path={ROUTES.filials.fullPath}>
              <Route index element={<Filials />} />
              <Route path={ROUTES.filials.edit.path} element={<FilialEdit />}>
                <Route index element={<FilialSettings />} />
                <Route
                  path={ROUTES.filials.edit.pages.common.security}
                  element={<FilialSecurity />}
                />
                <Route path={ROUTES.filials.edit.pages.common.modules} element={<CustomPage />} />
                <Route path={ROUTES.filials.edit.pages.common.smtp} element={<FilialSMTP />} />
                <Route
                  path={ROUTES.filials.edit.pages.common.mailServers}
                  element={<MailServersPage />}
                />
                <Route
                  path={ROUTES.filials.edit.pages.common.mailingSetup}
                  element={<MailingSetupPage />}
                />
                <Route
                  path={ROUTES.filials.edit.pages.common.locale}
                  element={<LocaleTimezonePage />}
                />
                <Route
                  path={ROUTES.filials.edit.pages.common.diskSpace}
                  element={<DiskSpasePage />}
                />
                <Route
                  path={ROUTES.filials.edit.pages.subRoutes.quotas.fullPath}
                  element={<SetupQuota />}
                />
                <Route path={ROUTES.filials.edit.pages.auth.ldap} element={<FilialLDAP />} />
                <Route path={ROUTES.filials.edit.pages.auth.invite} element={<InviteLinksList />} />
                <Route path={ROUTES.filials.edit.pages.modules.email} element={<EmailModule />} />
                <Route
                  path={ROUTES.filials.edit.pages.modules.calendar}
                  element={<CalendarModule />}
                />
                <Route path={ROUTES.filials.edit.pages.modules.draw} element={<DrawModule />} />
                <Route
                  path={ROUTES.filials.edit.pages.modules.projects}
                  element={<ProjectsModule />}
                />
                <Route
                  path={ROUTES.filials.edit.pages.modules.contacts}
                  element={<ContactsModule />}
                />
                <Route path={ROUTES.filials.edit.pages.modules.pages} element={<PagesModule />} />
                <Route path={ROUTES.filials.edit.pages.modules.forms} element={<FormsModule />} />
              </Route>
            </Route>
            <Route path={ROUTES.statistics.fullPath} element={<Statistics />}>
              <Route path={ROUTES.statistics.links.activity} element={<Activity />} />
              <Route path={ROUTES.statistics.links.diskSpace} element={<DiskSpace />} />
              <Route path={ROUTES.statistics.links.audit} element={<Audit />} />
            </Route>
          </Route>
          {isSaaS(Settings) && (
            <Route path="/register" element={<RedirectIFAuth />}>
              <Route path="/register" element={<Register />} />
            </Route>
          )}
          <Route path="/register/:code" element={<RedirectIFAuth />}>
            <Route path="/register/:code" element={<InviteRegister />} />
          </Route>
          <Route path="/login" element={<RedirectIFAuth />}>
            <Route path="/login" element={<Login />} />
          </Route>
          <Route path="/confirm-email/:code" element={<ConfirmEmail />} />
          <Route path="/password-reset/:code" element={<ResetPassword />} />
          <Route path="/password-reset" element={<RequestResetPassword />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
